/**
 * Прочая обработка детали для 3D: notches, roundNotches, quarters.
 * Та же СК, что у holes/grooves: X=length, Y=width, Z=толщина.
 */
import {Path} from "three";
import {createShape} from "../../../customer/processing/DetailProcessing/Canvas/CanvasCalcPositions/createShape";
import {orderContourPoints} from "../../../../utils/processing/orderContourPoints";
import {dedupeMachiningById} from "./machiningDedup";

const FACE_POSITIONS = new Set(["TOP", "BOTTOM"]);

const normalizeContourPoints = (points = []) =>
    orderContourPoints(points).map((p) => ({
        ...p,
        y: Number(p?.y) || 0,
        z: Number(p?.z) || 0,
        arcParams: p?.arcParams
            ? {...p.arcParams, y: Number(p.arcParams.y) || 0}
            : p?.arcParams,
    }));

const getFace = (item) => {
    const pos = item?.contour?.position;
    if (FACE_POSITIONS.has(pos)) return pos;
    return "TOP";
};

const isThroughDepth = (depth, panelHeightMm) => {
    const d = Number(depth);
    const height = Number(panelHeightMm);
    if (!Number.isFinite(height) || height <= 0) return false;
    if (!Number.isFinite(d)) return true; // как у отверстий без depth
    return d >= height - 0.5;
};

const contourShape = (item) => {
    const points = item?.contour?.points;
    if (!points?.length) return null;
    return createShape(normalizeContourPoints(points));
};

/** Прямоугольный вырез/четверть без contour.points — из basePoint + length/width. */
const rectContourShape = (item) => {
    const x0 = Number(item?.basePoint?.x) || 0;
    const y0 = Number(item?.basePoint?.y) || 0;
    const length = Number(item?.length) || 0;
    const width = Number(item?.width) || 0;
    if (length <= 0 || width <= 0) return null;
    return createShape(normalizeContourPoints([
        {x: x0, y: y0},
        {x: x0 + length, y: y0},
        {x: x0 + length, y: y0 + width},
        {x: x0, y: y0 + width},
        {x: x0, y: y0},
    ]));
};

const shapeForContourItem = (item) =>
    contourShape(item) || rectContourShape(item);

const blindPoseForContour = (item, heightMm, keyPrefix) => {
    const height = Math.max(Number(heightMm) || 18, 1);
    const depth = Math.min(
        Math.max(Number(item.depth) || height * 0.4, 0.1),
        height - 0.05,
    );
    const face = getFace(item);
    const z = face === "BOTTOM" ? 0.05 : height - depth - 0.05;
    const shape = shapeForContourItem(item);
    if (!shape) return null;
    return {
        key: item.id ?? `${keyPrefix}-blind-${face}`,
        shape,
        depth,
        z,
        face,
        kind: "contour",
    };
};

const classifyContourList = (list, height, keyPrefix) => {
    const throughFace = [];
    const blindFace = [];
    for (const item of dedupeMachiningById(list)) {
        if (!shapeForContourItem(item)) continue;
        if (isThroughDepth(item.depth, height)) {
            throughFace.push(item);
        } else {
            const pose = blindPoseForContour(item, height, keyPrefix);
            if (pose) blindFace.push(pose);
        }
    }
    return {throughFace, blindFace};
};

const isRenderableRoundNotch = (item) =>
    Boolean(
        item
        && item.center != null
        && item.radius != null
        && Number(item.radius) > 0,
    );

const classifyRoundNotches = (list, height) => {
    const throughFace = [];
    const blindFace = [];
    for (const item of dedupeMachiningById(list)) {
        if (!isRenderableRoundNotch(item)) continue;
        if (isThroughDepth(item.depth, height)) {
            throughFace.push(item);
        } else {
            const face = getFace(item);
            const depth = Math.min(
                Math.max(Number(item.depth) || height * 0.4, 0.1),
                Math.max(Number(height) || 18, 1) - 0.05,
            );
            const z = face === "BOTTOM"
                ? depth / 2 + 0.05
                : Math.max(Number(height) || 18, 1) - depth / 2 - 0.05;
            blindFace.push({
                key: item.id ?? `round-notch-blind-${face}`,
                kind: "round",
                item,
                face,
                depth,
                position: [
                    Number(item.center?.x) || 0,
                    Number(item.center?.y) || 0,
                    z,
                ],
                rotation: [Math.PI / 2, 0, 0],
                radius: Number(item.radius) || 0,
            });
        }
    }
    return {throughFace, blindFace};
};

/**
 * @returns {{
 *   throughContours: object[],
 *   blindContourPoses: object[],
 *   throughRounds: object[],
 *   blindRoundPoses: object[],
 *   height: number,
 * }}
 */
export const classifyDetailOtherMachining = (detailData) => {
    const height = Number(detailData?.height) || 18;
    const notches = classifyContourList(detailData?.notches, height, "notch");
    const quarters = classifyContourList(detailData?.quarters, height, "quarter");
    const rounds = classifyRoundNotches(detailData?.roundNotches, height);

    return {
        throughContours: [...notches.throughFace, ...quarters.throughFace],
        blindContourPoses: [...notches.blindFace, ...quarters.blindFace],
        throughRounds: rounds.throughFace,
        blindRoundPoses: rounds.blindFace,
        height,
    };
};

export const otherThroughContourShape = (item) => shapeForContourItem(item);

export const otherThroughRoundPath = (item) => {
    const r = Number(item?.radius) || 0;
    if (r <= 0) return null;
    return new Path().absellipse(
        Number(item.center?.x) || 0,
        Number(item.center?.y) || 0,
        r,
        r,
        0,
        Math.PI * 2,
    );
};
